import { Title } from "@solidjs/meta";
import twelveLogoWithText from "@assets/images/twelve-logo-with-text.svg?url";
import {
  createSignal,
  For,
  Index,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
  type JSX,
} from "solid-js";
import { Button, ButtonA, IconButtonA } from "~/common/button";
import { Icon } from "~/icon";
import { router } from "~/inertia";
import {
  publicReferralLetterDownloadPath,
  publicReferralLetterImagePath,
  publicReferralLetterPagePath,
  publicReferralLetterReportPath,
  publicReferralLetterViewPath,
} from "~/routes";

// pages: the PDF pages with a thumbnail drawn at export (the first 20; none for a letter exported before that)
type LetterFile =
  | { kind: "report"; label: string; pages: number[]; pageCount: number }
  | { kind: "image"; position: number; label: string };
type ReportFile = Extract<LetterFile, { kind: "report" }>;
type ImageFile = Extract<LetterFile, { kind: "image" }>;

type LocationHeader = {
  locationName: string;
  logoUrl: string | null;
  addressLines: string[] | null;
  phone: string | null;
};

// the letter's type and image count, the only things the page shows before View report
type Contents = { letterLabel: string; imageCount: number };

// render_page's states. An unavailable link names its office only when the token was real; a forged
// one has none. The patient is named nowhere outside the PDF.
type Props = { token: string; errors?: { page?: string } } & (
  | { state: "unavailable"; location: LocationHeader | null }
  | {
      state: "landing" | "viewer";
      location: LocationHeader;
      senderName: string;
      sentOn: string;
      contents: Contents;
      files: LetterFile[];
    }
);
type ReportProps = Extract<Props, { state: "landing" | "viewer" }>;

type Selected = { kind: "report"; page: number } | { kind: "image"; position: number };

const XRAY_TILE = "bg-[radial-gradient(45%_60%_at_50%_42%,#3a414b_0%,#1d2229_55%,#0e1115_100%)]";
const RING_ACTIVE = "shadow-[0_0_0_2px_var(--color-brand-primary)]";
const RING_PAGE = "shadow-[0_0_0_1px_var(--color-brand-divider)]";
// the locked page's text fades out under the button; Safari before 15.4 knows only the prefixed mask
const FADE: JSX.CSSProperties = {
  "-webkit-mask-image": "linear-gradient(#000 0%, transparent 85%)",
  "mask-image": "linear-gradient(#000 0%, transparent 85%)",
};

// A referring practice opens a report-back letter here from an emailed link: the report page with its
// files locked until View report, then the approved PDF and images in the same place. The office is
// the brand; Twelve sits small in the corner. Front-desk machines can be old, so this keeps to plain
// markup and nothing past the older-browser floor (no container queries: the letter page scales off
// its measured width instead).
export default function SecureLetter(props: Props) {
  const desktop = createMediaQuery("(min-width: 768px)");

  return (
    <Switch>
      {/* one Match for both, so View report fills the files in without rebuilding the page */}
      <Match when={props.state !== "unavailable" && props}>
        {(report) => (
          <>
            <Title>{report().contents.letterLabel}</Title>
            <Report {...report()} desktop={desktop()} />
          </>
        )}
      </Match>
      <Match when={props.state === "unavailable" && props}>
        {(dead) => (
          <>
            <Title>Link unavailable</Title>
            <Unavailable location={dead().location} desktop={desktop()} />
          </>
        )}
      </Match>
    </Switch>
  );
}

// One layout at a time rather than both behind CSS breakpoints: the opened page embeds the PDF, and a
// hidden second copy would fetch it (and log it) twice.
function createMediaQuery(query: string) {
  const list = window.matchMedia(query);
  const [matches, setMatches] = createSignal(list.matches);
  const update = () => setMatches(list.matches);
  list.addEventListener("change", update);
  onCleanup(() => list.removeEventListener("change", update));
  return matches;
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");
}

function OfficeTile(props: { location: LocationHeader }) {
  return (
    <Show
      when={props.location.logoUrl}
      fallback={
        <div class="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-brand-active text-xs font-bold text-white">
          {initials(props.location.locationName)}
        </div>
      }
    >
      {(url) => (
        <img
          src={url()}
          alt={props.location.locationName}
          class="size-8 shrink-0 rounded-[9px] object-contain"
        />
      )}
    </Show>
  );
}

function PoweredBy(props: { class?: string }) {
  return (
    <a
      href="https://twelve.ai"
      target="_blank"
      rel="noopener noreferrer"
      class={`flex shrink-0 gap-1.5 leading-none text-typography-tertiary ${props.class ?? "items-center text-xs"}`}
    >
      <span>Powered by</span>
      <img src={twelveLogoWithText} alt="Twelve" class="h-3 w-auto" />
    </a>
  );
}

// the office, and under it what this is and who sent it; the right side holds the page's actions
function TopBar(props: {
  location: LocationHeader;
  desktop: boolean;
  title?: string;
  subtitle?: string;
  right?: JSX.Element;
}) {
  return (
    <header
      class={
        props.desktop
          ? "flex h-[68px] shrink-0 items-center justify-between gap-6 border-b border-brand-divider bg-white pr-5 pl-6"
          : "flex shrink-0 items-center justify-between gap-3 border-b border-brand-divider bg-white pt-[max(12px,env(safe-area-inset-top))] pr-3 pb-3 pl-4"
      }
    >
      <div class={`flex min-w-0 items-center ${props.desktop ? "gap-3" : "gap-2.5"}`}>
        <OfficeTile location={props.location} />
        <Show
          when={props.title}
          fallback={
            <span
              class={`truncate font-semibold tracking-[-0.01em] ${props.desktop ? "text-base" : "text-[15px]"}`}
            >
              {props.location.locationName}
            </span>
          }
        >
          {(title) => (
            <div class="flex min-w-0 flex-col gap-px">
              <span
                class={`truncate font-semibold ${props.desktop ? "text-base tracking-[-0.01em]" : "text-[15px]"}`}
              >
                {title()}
              </span>
              <span
                class={`truncate text-typography-secondary ${props.desktop ? "text-[13px]" : "text-xs"}`}
              >
                {props.subtitle}
              </span>
            </div>
          )}
        </Show>
      </div>
      {props.right}
    </header>
  );
}

// The report is the page. Until View report (a POST, so link scanners never get through) it is the
// office's letterhead over a locked page, fetching nothing; after it, the PDF fills the well and the
// rail holds a thumbnail of every page and image.
function Report(props: ReportProps & { desktop: boolean }) {
  const [opening, setOpening] = createSignal(false);
  const opened = () => props.state === "viewer";
  const open = () => {
    if (opening()) return;
    setOpening(true);
    router.post(
      publicReferralLetterViewPath(props.token),
      {},
      { preserveScroll: true, onFinish: () => setOpening(false) },
    );
  };
  const office = () => props.location.locationName;
  const subtitle = () =>
    !opened()
      ? `${office()} · ${props.senderName}`
      : props.desktop
        ? `${office()} · ${props.senderName} · ${props.sentOn}`
        : `${office()} · ${props.sentOn.replace(/, \d{4}$/, "")}`;

  return (
    <div class="flex h-dvh flex-col bg-white text-typography-primary">
      <TopBar
        location={props.location}
        desktop={props.desktop}
        title={props.contents.letterLabel}
        subtitle={subtitle()}
        right={
          <Show
            when={opened()}
            fallback={
              <Show when={props.desktop}>
                <PoweredBy />
              </Show>
            }
          >
            <Show
              when={props.desktop}
              fallback={
                <IconButtonA
                  href={publicReferralLetterDownloadPath(props.token)}
                  iconName="download"
                  aria-label="Download all"
                  variant="primary"
                  square
                  class="size-11! shrink-0"
                />
              }
            >
              <div class="flex shrink-0 items-center gap-2">
                <Button
                  variant="secondary"
                  size="small"
                  leftIconName="printer"
                  onClick={() => printReport(publicReferralLetterReportPath(props.token))}
                >
                  Print
                </Button>
                <ButtonA
                  href={publicReferralLetterDownloadPath(props.token)}
                  variant="primary"
                  size="small"
                  leftIconName="download"
                >
                  Download all
                </ButtonA>
              </div>
            </Show>
          </Show>
        }
      />
      <Show when={opened()} fallback={<Locked {...props} opening={opening()} onOpen={open} />}>
        <Viewer {...props} />
      </Show>
    </div>
  );
}

// The browser prints the PDF itself: a hidden frame loads it and prints that, so the page's own
// chrome never ends up on paper. One frame, reused; a browser that won't print a frame opens it instead.
function printReport(href: string) {
  document.getElementById("report-print-frame")?.remove();
  const frame = document.createElement("iframe");
  frame.id = "report-print-frame";
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  frame.src = href;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      window.open(href, "_blank", "noopener");
    }
  };
  document.body.appendChild(frame);
}

// ---- locked ----

function Locked(props: ReportProps & { desktop: boolean; opening: boolean; onOpen: () => void }) {
  const address = () => props.location.addressLines ?? [];
  const contact = () => [...address(), props.location.phone].filter(Boolean).join(" · ");
  const viewButton = (full: boolean) => (
    <div class="flex flex-col items-start gap-3">
      <Button
        variant="primary"
        leftIconName="lock-closed"
        width={full ? "full" : undefined}
        class="h-11!"
        onClick={() => props.onOpen()}
        disabled={props.opening}
      >
        View report
      </Button>
      <Show when={props.errors?.page}>
        {(error) => <span class="text-sm text-brand-danger-dark">{error()}</span>}
      </Show>
    </div>
  );

  return (
    <Show
      when={props.desktop}
      fallback={
        <>
          {/* the well scrolls, so a short screen (a phone on its side) can still reach View report */}
          <div class="min-h-0 flex-1 overflow-y-auto bg-brand-light px-3 pt-3">
            <div class="flex min-h-full flex-col bg-white px-[22px] pt-7 pb-10 shadow-sm">
              <div class="flex flex-col gap-0.5 border-b-[1.5px] border-brand-active pb-2.5">
                <span class="text-sm font-bold tracking-[-0.01em]">
                  {props.location.locationName}
                </span>
                <Show when={contact()}>
                  <span class="text-[11px] text-typography-secondary">{contact()}</span>
                </Show>
              </div>
              <div class="mt-[22px] text-[26px] leading-[1.1] font-bold tracking-[-0.025em]">
                {props.contents.letterLabel}
              </div>
              <div class="mt-1.5 text-sm leading-[1.45] text-typography-secondary">
                Sent by {props.senderName} · {props.sentOn}
              </div>
              <div class="mt-5">{viewButton(true)}</div>
              <BlurredLines gap="gap-2.5" height="h-[5px]" blur="blur-[1px]" />
            </div>
          </div>
          <div class="flex shrink-0 items-end gap-3 overflow-x-auto border-t border-brand-divider bg-white px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
            <div class="flex w-[52px] shrink-0 flex-col gap-1">
              <PageTile class="w-[52px]" small />
              <span class="text-[11px] font-medium text-typography-secondary">Report</span>
            </div>
            <Show when={props.contents.imageCount > 0}>
              <div class="flex shrink-0 flex-col gap-1">
                <div class="flex gap-2">
                  <Index each={Array.from({ length: props.contents.imageCount })}>
                    {() => <LockedImageTile class="w-16" size="xs" />}
                  </Index>
                </div>
                <span class="text-[11px] font-medium text-typography-secondary">
                  {imagesCaption(props.contents.imageCount)}
                </span>
              </div>
            </Show>
          </div>
        </>
      }
    >
      <div class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_148px]">
        <div class="min-h-0 overflow-y-auto bg-brand-light px-8 pt-8">
          <ScaledPage class="aspect-[8.5/11] bg-white shadow-sm">
            {(u) => (
              <div class="flex flex-col" style={{ padding: `${u(8.5)} ${u(9)} 0` }}>
                <div
                  class="flex items-end justify-between border-brand-active leading-[1.55]"
                  style={{
                    "padding-bottom": u(2.4),
                    "border-bottom-width": u(0.2),
                    "font-size": u(1.7),
                  }}
                >
                  <span class="font-bold tracking-[-0.02em]" style={{ "font-size": u(2.6) }}>
                    {props.location.locationName}
                  </span>
                  <div class="text-right text-typography-secondary">
                    <For each={address()}>
                      {(line, index) => (
                        <div>
                          {line}
                          {index() === address().length - 1 && props.location.phone
                            ? ` · ${props.location.phone}`
                            : ""}
                        </div>
                      )}
                    </For>
                    <Show when={address().length === 0 && props.location.phone}>
                      <div>{props.location.phone}</div>
                    </Show>
                  </div>
                </div>
                <div
                  class="leading-[1.1] font-bold tracking-[-0.025em]"
                  style={{ "margin-top": u(5), "font-size": u(4) }}
                >
                  {props.contents.letterLabel}
                </div>
                <div
                  class="text-typography-secondary"
                  style={{ "margin-top": u(1.2), "font-size": u(1.9) }}
                >
                  Sent by {props.senderName} · {props.sentOn}
                </div>
                <div style={{ "margin-top": u(3.6) }}>{viewButton(false)}</div>
                <div
                  aria-hidden="true"
                  class="flex flex-col blur-[1.5px]"
                  style={{ ...FADE, "margin-top": u(4.4), gap: u(2.2) }}
                >
                  <For
                    each={["100%", "88%", "94%", "61%", "24%", "100%", "92%", "80%", "97%", "55%"]}
                  >
                    {(width) => (
                      <span
                        class="bg-gray-200"
                        style={{ height: u(1.1), width, "border-radius": u(0.4) }}
                      />
                    )}
                  </For>
                </div>
              </div>
            )}
          </ScaledPage>
        </div>
        <div class="flex min-h-0 flex-col gap-[18px] overflow-hidden border-l border-brand-divider p-5">
          <div class="flex flex-col gap-1.5">
            <PageTile class="w-full" locked />
            <span class="text-xs font-medium text-typography-secondary">
              {props.contents.letterLabel}
            </span>
          </div>
          <Show when={props.contents.imageCount > 0}>
            <div class="flex flex-col gap-1.5">
              <Index each={Array.from({ length: props.contents.imageCount })}>
                {() => <LockedImageTile class="w-full" size="sm" />}
              </Index>
              <span class="text-xs font-medium text-typography-secondary">
                {imagesCaption(props.contents.imageCount)}
              </span>
            </div>
          </Show>
        </div>
      </div>
    </Show>
  );
}

function imagesCaption(count: number) {
  return `${count} ${count === 1 ? "image" : "images"}`;
}

// A letter page that scales with its width like paper: every size is a share of the page width,
// measured here and handed to the children as `u(share)` (in px), the same as the design's cqw.
function ScaledPage(props: {
  class: string;
  children: (u: (share: number) => string) => JSX.Element;
}) {
  const [width, setWidth] = createSignal(0);
  let page!: HTMLDivElement;
  onMount(() => {
    const observer = new ResizeObserver(() => setWidth(page.clientWidth));
    observer.observe(page);
    setWidth(page.clientWidth);
    onCleanup(() => observer.disconnect());
  });
  const u = (share: number) => `${(width() * share) / 100}px`;
  return (
    <div ref={page} class={props.class}>
      {props.children(u)}
    </div>
  );
}

function BlurredLines(props: { gap: string; height: string; blur: string }) {
  return (
    <div aria-hidden="true" class={`mt-6 flex flex-col ${props.gap} ${props.blur}`} style={FADE}>
      <For each={["100%", "86%", "93%", "58%", "30%", "100%", "80%"]}>
        {(width) => <span class={`rounded-sm bg-gray-200 ${props.height}`} style={{ width }} />}
      </For>
    </div>
  );
}

// a stand-in page: a letterhead rule over faint lines, with a lock badge until the letter is opened
function PageTile(props: { class: string; locked?: boolean; small?: boolean }) {
  return (
    <div
      class={`relative flex aspect-[8.5/11] flex-col rounded-[3px] bg-white ${RING_PAGE} ${props.class} ${props.small ? "gap-1 px-[7px] py-2" : "gap-1.5 px-3 py-3.5"}`}
    >
      <span class={`w-[55%] rounded-[1px] bg-brand-active ${props.small ? "h-[3px]" : "h-1"}`} />
      <span class={`h-px w-full bg-brand-active ${props.small ? "mb-[3px]" : "mb-1.5"}`} />
      <Show when={!props.small}>
        <span class="h-1.5 w-[70%] rounded-[1px] bg-brand-subtle" />
      </Show>
      <span
        class={`w-full rounded-[1px] bg-gray-200 ${props.small ? "h-0.5" : "mt-1.5 h-[3px]"}`}
      />
      <span class={`w-[86%] rounded-[1px] bg-gray-200 ${props.small ? "h-0.5" : "h-[3px]"}`} />
      <Show when={!props.small}>
        <span class="h-[3px] w-[92%] rounded-[1px] bg-gray-200" />
      </Show>
      <Show when={props.locked}>
        <div class="absolute right-1.5 bottom-1.5 flex size-[22px] items-center justify-center rounded-full bg-white text-typography-tertiary shadow-xs">
          <Icon name="lock-closed" size="xs" />
        </div>
      </Show>
    </div>
  );
}

function LockedImageTile(props: { class: string; size: "xs" | "sm" }) {
  return (
    <div
      class={`flex aspect-[4/3] items-center justify-center rounded-[3px] text-typography-tertiary ${XRAY_TILE} ${props.class}`}
    >
      <Icon name="lock-closed" size={props.size} />
    </div>
  );
}

// ---- opened ----

function Viewer(props: ReportProps & { desktop: boolean }) {
  const report = () => props.files.find((file): file is ReportFile => file.kind === "report");
  const images = () => props.files.filter((file): file is ImageFile => file.kind === "image");
  // picking what is already showing changes nothing, so the PDF embed isn't rebuilt and fetched again
  const [selected, setSelected] = createSignal<Selected>(
    { kind: "report", page: 1 },
    { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );
  const selectedImage = () => {
    const current = selected();
    return current.kind === "image"
      ? images().find((image) => image.position === current.position)
      : undefined;
  };
  const pageCount = () => report()?.pageCount ?? 0;
  const reportHref = (download = false) =>
    publicReferralLetterReportPath(props.token, download ? { download: 1 } : {});
  // the download is its own request, so the staff history counts it apart from a preview
  const imageHref = (image: ImageFile, download = false) =>
    publicReferralLetterImagePath(props.token, image.position, download ? { download: 1 } : {});
  const pageHref = (page: number) => publicReferralLetterPagePath(props.token, page);
  const currentHref = (download = false) => {
    const image = selectedImage();
    return image ? imageHref(image, download) : reportHref(download);
  };
  const isPage = (page: number) => {
    const current = selected();
    return current.kind === "report" && current.page === page;
  };
  const isImage = (image: ImageFile) => {
    const current = selected();
    return current.kind === "image" && current.position === image.position;
  };

  const well = (
    <div class="relative min-h-0 flex-1">
      <Show
        when={selectedImage()}
        fallback={
          // The browser's own PDF viewer, fit to width. A thumbnail jumps to its page through the url,
          // so each jump is a fresh embed; where there's no viewer (an old iPad) the link still opens it.
          <Show when={selected().kind === "report" && selected()} keyed>
            {(current) => (
              <object
                data={`${reportHref()}#page=${current.kind === "report" ? current.page : 1}&view=FitH&toolbar=0&navpanes=0`}
                type="application/pdf"
                aria-label={props.contents.letterLabel}
                class="absolute inset-0 block size-full bg-brand-light"
              >
                <div class="flex h-full items-center justify-center p-6 text-sm">
                  <a href={reportHref()} target="_blank" rel="noopener" class="underline">
                    Open the report
                  </a>
                </div>
              </object>
            )}
          </Show>
        }
      >
        {(image) => (
          <div class="absolute inset-0 flex overflow-auto bg-[#0e1115]">
            <img
              src={imageHref(image())}
              alt={image().label}
              class={`m-auto max-h-full max-w-full rounded object-contain ${props.desktop ? "p-10 pb-24" : ""}`}
            />
          </div>
        )}
      </Show>
      <Show when={props.desktop}>
        <div class="absolute right-5 bottom-5 flex h-10 items-center gap-0.5 rounded-full bg-white px-1.5 shadow-md">
          <Show when={!selectedImage() && pageCount() > 0}>
            <span class="px-2 text-[13px] font-medium text-typography-secondary tabular-nums">
              {pageCount()} {pageCount() === 1 ? "page" : "pages"}
            </span>
            <span class="mx-1.5 h-[18px] w-px bg-brand-divider" />
          </Show>
          <IconButtonA
            href={currentHref()}
            target="_blank"
            rel="noopener"
            iconName="arrows-pointing-out"
            aria-label="Open full screen"
            title="Open full screen"
            variant="tertiary"
            size="small"
            square
          />
          <span class="mx-1.5 h-[18px] w-px bg-brand-divider" />
          <IconButtonA
            href={currentHref(true)}
            iconName="download"
            aria-label="Download this file"
            title="Download this file"
            variant="tertiary"
            size="small"
            square
          />
        </div>
      </Show>
    </div>
  );

  return (
    <Show
      when={props.desktop}
      fallback={
        <>
          {well}
          <div class="flex shrink-0 items-end gap-3 overflow-x-auto border-t border-brand-divider bg-white px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
            <button
              type="button"
              class="flex w-[52px] shrink-0 flex-col gap-1 text-left"
              onClick={() => setSelected({ kind: "report", page: 1 })}
            >
              <Show
                when={report()?.pages.length}
                fallback={
                  <div class={`rounded-[3px] ${selected().kind === "report" ? RING_ACTIVE : ""}`}>
                    <PageTile class="w-[52px]" small />
                  </div>
                }
              >
                <img
                  src={pageHref(1)}
                  alt=""
                  class={`w-[52px] rounded-[3px] bg-white ${selected().kind === "report" ? RING_ACTIVE : RING_PAGE}`}
                />
              </Show>
              <span class="text-[11px] font-medium text-typography-secondary">Report</span>
            </button>
            <For each={images()}>
              {(image) => (
                <button
                  type="button"
                  class="flex shrink-0 flex-col gap-1 text-left"
                  onClick={() => setSelected({ kind: "image", position: image.position })}
                >
                  <img
                    src={imageHref(image)}
                    alt={image.label}
                    class={`h-[69px] w-auto max-w-[96px] rounded-[3px] bg-[#0e1115] object-cover ${isImage(image) ? RING_ACTIVE : ""}`}
                  />
                  <span class="max-w-[96px] truncate text-[11px] font-medium text-typography-secondary">
                    {image.label}
                  </span>
                </button>
              )}
            </For>
            <IconButtonA
              href={currentHref(true)}
              iconName="download"
              aria-label="Download this file"
              variant="secondary"
              square
              class="ml-auto size-11! shrink-0 self-center"
            />
          </div>
        </>
      }
    >
      <div class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_148px]">
        <div class="flex min-h-0 flex-col">{well}</div>
        <div class="flex min-h-0 flex-col gap-[18px] overflow-auto border-l border-brand-divider px-5 pt-5 pb-4">
          <Show
            when={report()?.pages.length}
            fallback={
              <button
                type="button"
                class="flex flex-col gap-1.5 text-left"
                onClick={() => setSelected({ kind: "report", page: 1 })}
              >
                <div class={`rounded-[3px] ${selected().kind === "report" ? RING_ACTIVE : ""}`}>
                  <PageTile class="w-full" />
                </div>
                <span class="text-xs font-medium text-typography-secondary">
                  {props.contents.letterLabel}
                </span>
              </button>
            }
          >
            <For each={report()?.pages}>
              {(page) => (
                <button
                  type="button"
                  class="flex flex-col gap-1.5 text-left"
                  onClick={() => setSelected({ kind: "report", page })}
                >
                  <img
                    src={pageHref(page)}
                    alt=""
                    class={`w-full rounded-[3px] bg-white ${isPage(page) ? RING_ACTIVE : RING_PAGE}`}
                  />
                  <span class="text-xs font-medium text-typography-secondary">
                    Report · p. {page}
                  </span>
                </button>
              )}
            </For>
          </Show>
          <For each={images()}>
            {(image) => (
              <button
                type="button"
                class="flex flex-col gap-1.5 text-left"
                onClick={() => setSelected({ kind: "image", position: image.position })}
              >
                <img
                  src={imageHref(image)}
                  alt={image.label}
                  class={`w-full rounded-[3px] bg-[#0e1115] ${isImage(image) ? RING_ACTIVE : ""}`}
                />
                <span class="text-xs font-medium text-typography-secondary">{image.label}</span>
              </button>
            )}
          </For>
          <PoweredBy class="mt-auto flex-col items-start pt-3 text-[11px]" />
        </div>
      </div>
    </Show>
  );
}

// ---- unavailable ----

function Unavailable(props: { location: LocationHeader | null; desktop: boolean }) {
  const phoneHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, "")}`;
  return (
    <div class="flex min-h-dvh flex-col bg-white text-typography-primary">
      <Show when={props.location}>
        {(location) => (
          <TopBar
            location={location()}
            desktop={props.desktop}
            right={props.desktop ? <PoweredBy /> : undefined}
          />
        )}
      </Show>
      <main
        class={`flex flex-1 flex-col items-center justify-center bg-brand-light text-center ${props.desktop ? "p-8" : "px-7 pt-6 pb-12"}`}
      >
        <div class="flex w-full max-w-[460px] flex-col items-center">
          <div class="flex size-12 items-center justify-center rounded-full bg-white text-brand-primary shadow-xs">
            <Icon name="link-slash" size="lg" />
          </div>
          <h1
            class={`font-bold tracking-[-0.025em] ${props.desktop ? "mt-5 text-[28px] leading-[1.15]" : "mt-[18px] text-2xl leading-[1.2]"}`}
          >
            This link isn't available
          </h1>
          <p
            class={`mt-2.5 leading-normal text-pretty text-typography-secondary ${props.desktop ? "text-base" : "text-[15px]"}`}
          >
            It may have expired, been replaced by a newer link, or been turned off.{" "}
            {props.location
              ? `Ask ${props.location.locationName} to send you a new one.`
              : "Ask the office that sent it to send you a new one."}
          </p>
          <Show when={props.location?.phone}>
            {(phone) => (
              <div class="mt-6 w-full">
                <ButtonA
                  href={phoneHref(phone())}
                  variant="secondary"
                  leftIconName="phone"
                  width={props.desktop ? "fit" : "full"}
                  class={props.desktop ? "mx-auto" : "h-11!"}
                >
                  {props.desktop ? phone() : `Call ${phone()}`}
                </ButtonA>
              </div>
            )}
          </Show>
          <Show when={!props.location && props.desktop}>
            <PoweredBy class="mt-10 items-center text-xs" />
          </Show>
        </div>
      </main>
      <Show when={!props.desktop}>
        <div class="flex justify-center bg-brand-light pt-4 pb-[34px]">
          <PoweredBy />
        </div>
      </Show>
    </div>
  );
}
